import React from 'react'; //eslint-disable-line
import Grid from '@mui/material/Grid';
import { change, Field } from 'redux-form';
import TextFormField from '../../../../shared/forms/fields/TextFormField';
import SelectFormField from '../../../../shared/forms/fields/SelectFormField';
import { logLevelOptions } from '../../../../types/logLevel';
import MenuItem from '@mui/material/MenuItem';
import MultiTextFormField from '../../../../shared/forms/fields/MultiTextFormField';
import { EsmeBindMode } from '../../../../models/esmeModel';
import { ESME_FORM_NAME } from '../../../../store/form/formStateModel';
import { useAppDispatch } from '../../../../store/hooks';
import { ipv4Validator } from '../../../../shared/forms/validation/validators/ipv4Validator';
import { parseNumberFieldValue } from '../../../../shared/forms/utils/parseNumberFieldValue';

const generateEsmeFileLog = (esmeUsername: string): string => `esme_${esmeUsername}.log`;

type Props = {
  readonly readOnly: boolean;
};

const EsmeConfigFields = (props: Props) => {
  const { readOnly } = props;
  const dispatch = useAppDispatch();

  const changeEsmeLogFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const esmeUsername = event.target.value;

    dispatch(change(ESME_FORM_NAME, 'user.logFile', generateEsmeFileLog(esmeUsername)));
  };

  const bindModeOptions = [
    { label: 'all', value: EsmeBindMode.ALL },
    { label: 'transmitter', value: EsmeBindMode.TRANSMITTER },
    { label: 'receiver', value: EsmeBindMode.RECEIVER },
    { label: 'transceiver', value: EsmeBindMode.TRANSCEIVER },
    { label: 'EMI session', value: EsmeBindMode.EMI_SESSION },
    { label: 'transmitter (via TLS/SSL)', value: EsmeBindMode.TRANSMITTER_TLS_SSL },
    { label: 'receiver (via TLS/SSL)', value: EsmeBindMode.RECEIVER_TLS_SSL },
    { label: 'transceiver (via TLS/SSL)', value: EsmeBindMode.TRANSCEIVER_TLS_SSL },
  ];

  return (
    <Grid container spacing={4} style={{ paddingTop: 20 }}>
      <Grid item xs={6}>
        <Field
          fullWidth
          disabled={readOnly}
          type='text'
          label='Username'
          name='user.username'
          onChange={changeEsmeLogFile}
          component={TextFormField}
          required
        />
      </Grid>
      <Grid item xs={6}>
        <Field
          fullWidth
          disabled={readOnly}
          type='text'
          label='Password'
          name='user.password'
          component={TextFormField}
          required
        />
      </Grid>
      <Grid item xs={6}>
        <Field
          fullWidth
          disabled={readOnly}
          label='Log level'
          name='user.logLevel'
          component={SelectFormField}
          required
        >
          { logLevelOptions.map(logLevel => (
            <MenuItem key={logLevel} value={logLevel}>{logLevel}</MenuItem>
          ))}
        </Field>
      </Grid>
      <Grid item xs={6}>
        <Field
          fullWidth
          type='text'
          label='Log file'
          name='user.logFile'
          component={TextFormField}
          helperText='Self-generated based on esme username'
          disabled
          required
        />
      </Grid>
      <Grid item xs={3}>
        <Field
          fullWidth
          disabled={readOnly}
          type='number'
          min={0}
          label='Max binds'
          name='user.maxBinds'
          component={TextFormField}
          required
          parse={parseNumberFieldValue}
        />
      </Grid>
      <Grid item xs={3}>
        <Field
          fullWidth
          disabled={readOnly}
          type='number'
          min={0}
          label='Max TX binds'
          name='user.maxTransmitterBinds'
          component={TextFormField}
          parse={parseNumberFieldValue}
        />
      </Grid>
      <Grid item xs={3}>
        <Field
          fullWidth
          disabled={readOnly}
          type='number'
          min={0}
          label='Max RX binds'
          name='user.maxReceiverBinds'
          component={TextFormField}
          parse={parseNumberFieldValue}
        />
      </Grid>
      <Grid item xs={3}>
        <Field
          fullWidth
          disabled={readOnly}
          type='number'
          min={0}
          label='Max TRX binds'
          name='user.maxTransceiverBinds'
          component={TextFormField}
          parse={parseNumberFieldValue}
        />
      </Grid>
      <Grid item xs={6}>
        <Field
          fullWidth
          disabled={readOnly}
          label='Allowed IPs'
          name='user.userAllowIps'
          placeholder='e.g. 127.0.0.1'
          component={MultiTextFormField}
          itemValidator={ipv4Validator}
        />
      </Grid>
      <Grid item xs={6}>
        <Field
          format={(value: String) => value || []}
          fullWidth
          disabled={readOnly}
          label='Mode'
          name='user.bindModes'
          component={SelectFormField}
          multiple={true}
          required
        >
          { bindModeOptions.map(({label, value}) => (
            <MenuItem key={value} value={value}>{label}</MenuItem>
          ))}
        </Field>
      </Grid>
      <Grid item xs={3}>
        <Field
          fullWidth
          disabled={readOnly}
          type='number'
          min={0}
          label='Session timeout'
          name='user.sessionTimeout'
          component={TextFormField}
          required
          parse={parseNumberFieldValue}
        />
      </Grid>
      <Grid item xs={3}>
        <Field
          fullWidth
          disabled={readOnly}
          type='number'
          min={0}
          label='Throughput'
          name='user.throughput'
          component={TextFormField}
          required
          parse={parseNumberFieldValue}
        />
      </Grid>
      <Grid item xs={3}>
        <Field
          fullWidth
          disabled={readOnly}
          type='number'
          min={0}
          label='Throughput in'
          name='user.throughputIn'
          component={TextFormField}
          parse={parseNumberFieldValue}
        />
      </Grid>
      <Grid item xs={3}>
        <Field
          fullWidth
          disabled={readOnly}
          type='number'
          min={0}
          label='Throughput out'
          name='user.throughputOut'
          component={TextFormField}
          parse={parseNumberFieldValue}
        />
      </Grid>
    </Grid>
  );
};

export default EsmeConfigFields;
